import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { useLanguage } from '../../../contexts/LanguageContext';
import { cn } from '../../../utils/cn';
import 'flag-icons/css/flag-icons.min.css';

const languages = [
  { code: 'tr', label: 'Türkçe', flag: 'tr' },
  { code: 'en', label: 'English', flag: 'gb' },
];

export const LanguageSelector = () => {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentLang = languages.find((l) => l.code === language);

  const toggleDropdown = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const selectLanguage = useCallback((code) => {
    setLanguage(code);
    setIsOpen(false);
  }, [setLanguage]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors group"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Dil seçimi / Language selection"
      >
        <span className={`fi fi-${languages.find(l => l.code === language)?.flag} w-5 h-5 rounded-sm shadow-sm`} />
        <span className="text-sm font-medium text-gray-700 group-hover:text-primary transition-colors uppercase">
          {language}
        </span>
        <FiChevronDown
          className={cn(
            'text-gray-400 transition-transform duration-300',
            isOpen ? 'rotate-180' : ''
          )}
        />
      </button>

      <div
        className={cn(
          'absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50 transform transition-all duration-200 origin-top-right',
          isOpen ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
        )}
        role="listbox"
      >
        {languages.map(({ code, label, flag }) => (
          <button
            key={code}
            onClick={() => selectLanguage(code)}
            className={cn(
              'w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-gray-50',
              language === code ? 'text-primary font-semibold bg-primary/5' : 'text-gray-700'
            )}
            role="option"
            aria-selected={language === code}
          >
            <span className={`fi fi-${flag} w-5 h-5 rounded-sm shadow-sm`} />
            <span className="flex-1 text-left">{label}</span>
            {language === code && (
              <div className="w-1.5 h-1.5 rounded-full bg-primary" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
};
